import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { LEGAL_VERSIONS } from '../config/legalVersions';

export const COOKIE_CONSENT_VERSION = LEGAL_VERSIONS.COOKIE;

export const CONSENT_CATEGORIES = {
  necessary: { id: 'necessary', label: 'Strictly necessary', required: true, defaultGranted: true },
  analytics: { id: 'analytics', label: 'Analytics', required: false, defaultGranted: false },
  errorTracking: { id: 'errorTracking', label: 'Error tracking', required: false, defaultGranted: false },
};

const initialChoices = () => ({
  necessary: true,
  analytics: false,
  errorTracking: false,
});

export const useCookieConsentStore = create(
  persist(
    (set, get) => ({
      version: null,
      decided: false,
      decidedAt: null,
      bannerOpen: false,
      managerOpen: false,
      choices: initialChoices(),

      openBanner: () => set({ bannerOpen: true }),
      closeBanner: () => set({ bannerOpen: false }),
      openManager: () => set({ managerOpen: true, bannerOpen: false }),
      closeManager: () => set({ managerOpen: false }),

      acceptAll: () => {
        set({
          version: COOKIE_CONSENT_VERSION,
          decided: true,
          decidedAt: new Date().toISOString(),
          bannerOpen: false,
          managerOpen: false,
          choices: { necessary: true, analytics: true, errorTracking: true },
        });
        notifyConsentChange(get().choices);
      },

      rejectAll: () => {
        set({
          version: COOKIE_CONSENT_VERSION,
          decided: true,
          decidedAt: new Date().toISOString(),
          bannerOpen: false,
          managerOpen: false,
          choices: { necessary: true, analytics: false, errorTracking: false },
        });
        notifyConsentChange(get().choices);
      },

      saveChoices: (next) => {
        const choices = {
          necessary: true,
          analytics: Boolean(next?.analytics),
          errorTracking: Boolean(next?.errorTracking),
        };
        set({
          version: COOKIE_CONSENT_VERSION,
          decided: true,
          decidedAt: new Date().toISOString(),
          bannerOpen: false,
          managerOpen: false,
          choices,
        });
        notifyConsentChange(choices);
      },

      ensureCurrentVersion: () => {
        const state = get();
        if (state.decided && state.version === COOKIE_CONSENT_VERSION) {
          notifyConsentChange(state.choices);
          return;
        }
        if (state.decided && state.version !== COOKIE_CONSENT_VERSION) {
          set({ decided: false, bannerOpen: true });
          return;
        }
        set({ bannerOpen: true });
      },
    }),
    {
      name: 'cookie-consent',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        version: state.version,
        decided: state.decided,
        decidedAt: state.decidedAt,
        choices: state.choices,
      }),
    },
  ),
);

const subscribers = new Set();

export function subscribeConsent(fn) {
  subscribers.add(fn);
  const state = useCookieConsentStore.getState();
  if (state.decided) {
    try { fn(state.choices); } catch { /* noop */ }
  }
  return () => subscribers.delete(fn);
}

function notifyConsentChange(choices) {
  for (const fn of subscribers) {
    try { fn(choices); } catch { /* noop */ }
  }
}

export function hasConsent(category) {
  const state = useCookieConsentStore.getState();
  if (!state.decided) return false;
  return Boolean(state.choices?.[category]);
}
